/**
 * CallTrackingMetrics PII Redaction Helper (Story 22.3, AC #5; Round 3
 * Red Team Attack #1)
 *
 * Mirrors the CallRail 22.1 `sanitizeCallRailPii` shape with CTM-specific
 * field names. CTM's `/calls.json`, `/forms.json`, and `/sms.json`
 * endpoints return per-record data with **caller-identifying information**
 * in the DEFAULT response (verified
 * `_bmad/bmm/data/integration-metrics/calltrackingmetrics.md` 2026-05-15).
 *
 * The adapter MUST run every record through `sanitizeCtmPii` BEFORE the
 * record enters:
 *  - `console.log` (structured log emissions)
 *  - Sentry breadcrumbs / `Sentry.captureException` context
 *  - Queue-error payloads (CloudFlare Queues dead-letter messages)
 *  - Any error message body returned to a downstream consumer
 *
 * The redacted-form record is the only shape allowed to leak. The
 * unredacted-form is in scope ONLY inside `fetchMetrics` → transformer →
 * return path — that path eventually writes to the tenant-scoped KV
 * cache and D1, where tenant access control governs read access.
 *
 * **PII field list — verified against the catalogue 2026-05-15:**
 *  - `caller_number` / `caller_number_format` / `caller_number_complete`
 *    — caller's phone (direct PII)
 *  - `name` — caller's name (direct PII)
 *  - `email` — caller's email (direct PII)
 *  - `street` / `city` / `state` / `postal_code` / `country` — geo PII
 *  - `geo` (nested `{ lat, lon }`) — geo PII
 *  - `transcription` / `transcription_text` — caller's spoken words
 *  - `summary` — AI-generated call summary; may contain caller
 *    identification
 *  - `notes` — free-form notes; agent-supplied but may contain PII
 *  - `spotted` — array of detected keywords (CI-gated)
 *  - `agent_insights` — AI-extracted highlights
 *  - `message` — SMS body (PII-equivalent because the caller types it)
 *  - `inbound_rate_center` — telco-level location identifier
 *  - `webvisit.location_host` / `webvisit.location_path` — visited URL
 *    that may embed caller-PII as query params
 *
 * **The `audio` recording URL field is NOT redacted by this helper.**
 * Recording URLs are stripped at the transformer (Story 22.3 AC #4 +
 * Round 3 Red Team Attack #2) — they never enter the persisted record
 * shape at all, so they cannot leak via this path.
 */

/**
 * Frozen Set of PII field names (snake_case wire format) to redact.
 * Defined as a Set for O(1) `.has()` lookups during recursive traversal.
 * Adding a new field is additive; removing a field requires explicit
 * justification + audit.
 *
 * **Story 35.4 expansion (Batch / AC #2):** 4 new flat fields added —
 * `call_path`, `last_touch`, `first_touch`, `last_location`. Set size
 * bumps from 20 → 24. The remaining 2 new shapes (`form.custom[].value`,
 * `transfers[].from`/`to`) are nested-array shapes handled by the
 * recursive helper below, NOT flat fields.
 */
export const CTM_PII_FIELDS: ReadonlySet<string> = new Set<string>([
  "caller_number",
  "caller_number_format",
  "caller_number_complete",
  "name",
  "email",
  "phone",                           // /forms.json caller phone (AC #16)
  "street",
  "city",
  "state",
  "postal_code",
  "country",
  "geo",
  "transcription",
  "transcription_text",
  "summary",
  "notes",
  "spotted",
  "agent_insights",
  "message",
  "inbound_rate_center",
  // Story 35.4 — Batch expansion (AC #2)
  "call_path",
  "last_touch",
  "first_touch",
  "last_location",
]);

/**
 * The lowerCamelCase forms of the PII fields above — used after the
 * transformer has normalized snake_case → lowerCamelCase. Both
 * representations are checked because PII flows through the helper at
 * BOTH the pre-transform stage (wire-format snake_case from CTM) and
 * the post-transform stage (records emitted to logs).
 *
 * Story 35.4 — Batch expansion mirrors the snake_case additions.
 */
export const CTM_PII_FIELDS_CAMEL: ReadonlySet<string> = new Set<string>([
  "callerNumber",
  "callerNumberFormat",
  "callerNumberComplete",
  "name",
  "email",
  "phone",
  "street",
  "city",
  "state",
  "postalCode",
  "country",
  "geo",
  "transcription",
  "transcriptionText",
  "summary",
  "notes",
  "spotted",
  "agentInsights",
  "message",
  "inboundRateCenter",
  // Story 35.4 — Batch expansion camelCase mirrors
  "callPath",
  "lastTouch",
  "firstTouch",
  "lastLocation",
]);

/**
 * Nested webvisit-object fields that may embed caller-PII as URL query
 * params. Redacted recursively when the outer record has a
 * `webvisit` / `webVisit` key.
 */
export const CTM_WEBVISIT_PII_FIELDS: ReadonlySet<string> = new Set<string>([
  "location_host",
  "locationHost",
  "location_path",
  "locationPath",
]);

/**
 * Reserved JS-runtime keys that, if assigned via `out[key] = ...`,
 * could mutate `Object.prototype` (in non-strict mode) or otherwise
 * confuse downstream consumers that perform property-existence checks.
 *
 * Mirrors CallRail 22.1's `FORBIDDEN_KEYS` defense — a malicious CTM
 * user could craft a custom field name like `__proto__` on their
 * account. Skip them entirely.
 */
const FORBIDDEN_KEYS: ReadonlySet<string> = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

/**
 * Story 35.4 AC #2 — nested-array shapes that require per-element
 * field-level redaction (NOT whole-field collapse). These are exempted
 * from the flat-list shortcut in `sanitizeCtmPii` so the recursive
 * handler can preserve safe sibling fields while wiping the PII-bearing
 * subset.
 *
 *  - `call_path` / `callPath` — array of `{route_name, route_id, route_type, started_at}`.
 *    Redact `route_name`; preserve `route_id` + `route_type` + `started_at`.
 *  - `form_custom` / `formCustom` / `custom` — array of `{id, value}`.
 *    Redact `value`; preserve `id`.
 *  - `transfers` — array of `{from, to, offset, status, start_time, transferred_at_relative}`.
 *    Redact `from` AND `to`; preserve everything else.
 *
 * Round 3 Red Team Attack #1 + Story 35.4 Structural Concern B —
 * legal-vertical customers frequently capture SSN-equivalent identifiers
 * in `form.custom[].value`; phone numbers in `transfers[].from/to` are
 * direct caller PII; route names in `call_path[].route_name` may embed
 * caller-PII via custom IVR naming.
 */
const NESTED_PII_ARRAY_FIELDS: ReadonlySet<string> = new Set([
  "call_path",
  "callPath",
  "custom",
  "form_custom",
  "formCustom",
  // **Round 3 fix:** the form transformer hoists `form.custom` →
  // `formCustomFields` to give downstream aggregators a stable shape.
  // Without this entry, the hoisted key bypasses the nested-array
  // redactor and `value` PII leaks at the log boundary.
  "formCustomFields",
  "transfers",
]);

/**
 * Per-element redaction recipe for the nested-PII-array shapes above.
 * Maps the parent-array field name (either snake_case or camelCase) to
 * the set of SUB-fields that must be redacted per element. Sibling
 * sub-fields not listed here are preserved.
 */
const NESTED_PII_ELEMENT_REDACT_KEYS: Record<string, ReadonlySet<string>> = {
  call_path: new Set(["route_name", "routeName"]),
  callPath: new Set(["route_name", "routeName"]),
  custom: new Set(["value"]),
  form_custom: new Set(["value"]),
  formCustom: new Set(["value"]),
  formCustomFields: new Set(["value"]),
  transfers: new Set(["from", "to"]),
};

/**
 * Apply per-element redaction to a nested-PII array. Pure function —
 * returns a new array; never mutates input.
 *
 * **Round 1 Edge Case Hunter fix (HIGH):** non-redacted siblings that
 * are themselves objects/arrays are now recursed through `sanitizeCtmPii`
 * to defend against PII embedded in nested children (e.g., future-CTM
 * shape `call_path[].caller_metadata.email`). Plain primitives are still
 * copied by value. The "Returns a NEW shallow copy — never mutates the
 * input" contract is now upheld for all nested element shapes, not just
 * primitives.
 */
function redactNestedPiiArray(
  parentKey: string,
  arr: ReadonlyArray<unknown>,
  depth = 1,
): unknown[] {
  const redactKeys = NESTED_PII_ELEMENT_REDACT_KEYS[parentKey];
  if (!redactKeys) return [...arr];
  return arr.map((item) => {
    if (item === null || typeof item !== "object") {
      return item;
    }
    // **Round 3 fix:** if a nested-PII-array element is itself an array
    // (shape drift — e.g., `transfers: [[{from}]]`), recurse through
    // `redactNestedPiiArray` so the inner elements still get
    // per-element redaction. Without this, the inner objects would pass
    // through unredacted via shallow copy.
    if (Array.isArray(item)) {
      return redactNestedPiiArray(parentKey, item, depth + 1);
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
      if (FORBIDDEN_KEYS.has(k)) continue;
      if (redactKeys.has(k)) {
        out[k] = "[redacted]";
        continue;
      }
      // Round 1 fix — recurse into object-valued siblings to catch PII
      // nested under non-redacted keys (e.g., a `caller_metadata: {email}`
      // sibling of `route_name`). Primitives are copied by value.
      if (v !== null && typeof v === "object" && !Array.isArray(v)) {
        out[k] = sanitizeCtmPii(v as Record<string, unknown>, depth + 1);
      } else if (Array.isArray(v)) {
        out[k] = v.map((nested) =>
          nested !== null && typeof nested === "object" && !Array.isArray(nested)
            ? sanitizeCtmPii(nested as Record<string, unknown>, depth + 1)
            : nested,
        );
      } else {
        out[k] = v;
      }
    }
    return out;
  });
}

/**
 * Redact PII fields from a CTM record. Returns a NEW shallow copy —
 * never mutates the input.
 *
 * Recursive: handles the nested `geo: { lat, lon }` and `webvisit: { ... }`
 * shapes that CTM responses use. Caps recursion depth at 4 to defend
 * against pathological self-referential inputs.
 *
 * Replacement value is the literal string `"[redacted]"` for all
 * redacted fields. We do NOT preserve type information (replacing a
 * string with a string vs replacing an array with a string) because the
 * redacted form is intended for LOG OUTPUT only — never for consumption
 * by downstream logic that would type-check the field. If a downstream
 * consumer needs the unredacted value, it reads from the `fetchMetrics`
 * return path BEFORE this helper runs.
 *
 * @param record - The raw record from CTM (or a transformed record).
 * @param depth - Internal recursion guard. Callers should NOT pass this.
 * @returns A shallow-copied record with PII fields replaced by `"[redacted]"`.
 */
export function sanitizeCtmPii<T extends Record<string, unknown>>(
  record: T,
  depth = 0,
): Record<string, unknown> {
  // Defensive recursion cap — pathological inputs cannot exhaust stack.
  // **Round 2 Edge Case Hunter HIGH fix:** when the depth cap fires, FAIL
  // CLOSED — collapse the entire subtree to `"[redacted]"` rather than
  // returning the raw record unredacted. The previous behavior was a
  // depth-amplification PII leak: an attacker could craft a 5+ level
  // nesting of `caller_metadata.a.b.c.phone` under `call_path[]` and the
  // helper would return the phone unredacted. Fail-closed makes that
  // class of attack impossible.
  if (depth > 4) {
    return { __redacted_truncated: true } as Record<string, unknown>;
  }
  if (record === null || typeof record !== "object") {
    return record as Record<string, unknown>;
  }

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (FORBIDDEN_KEYS.has(key)) continue;

    // Story 35.4 AC #2 — nested-PII-array shapes get per-element
    // redaction (preserve safe siblings). CHECK BEFORE the flat-list
    // shortcut so e.g. `call_path` arrays don't collapse to "[redacted]"
    // — instead `route_name` gets redacted per element while
    // `route_id` / `route_type` / `started_at` are preserved for analytics.
    if (NESTED_PII_ARRAY_FIELDS.has(key) && Array.isArray(value)) {
      out[key] = redactNestedPiiArray(key, value, depth + 1);
      continue;
    }
    // Round 2 Edge Case Hunter HIGH fix — `custom` MAY arrive as a
    // single object (not an array) per the CTM Postman collection
    // shape-variance note. When it does, treat as a single-element
    // array for per-element redaction (preserve `id`, redact `value`).
    if (
      NESTED_PII_ARRAY_FIELDS.has(key) &&
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      out[key] = redactNestedPiiArray(key, [value], depth + 1)[0];
      continue;
    }

    // PII allowlist match (either snake_case or camelCase form).
    // Flat-list shortcut: the entire field value collapses to "[redacted]".
    if (CTM_PII_FIELDS.has(key) || CTM_PII_FIELDS_CAMEL.has(key)) {
      out[key] = "[redacted]";
      continue;
    }

    // Pass Date / Map / Set / RegExp / Error through unchanged.
    if (
      value instanceof Date ||
      value instanceof Map ||
      value instanceof Set ||
      value instanceof RegExp ||
      value instanceof Error
    ) {
      out[key] = value;
      continue;
    }

    // Recurse into nested objects. `webvisit` / `webVisit` gets a
    // dedicated nested-shape redaction (location_host + location_path
    // may embed caller-PII as URL query params). `form` / `Form` may
    // contain a `custom` array of {id, value} that requires per-element
    // redaction — Story 35.4 AC #2.
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      if (key === "webvisit" || key === "webVisit") {
        out[key] = sanitizeWebvisitNested(
          value as Record<string, unknown>,
          depth + 1,
        );
      } else {
        out[key] = sanitizeCtmPii(
          value as Record<string, unknown>,
          depth + 1,
        );
      }
      continue;
    }

    // Arrays of objects (CTM `tag_list` is an array of strings,
    // `legs` / `transfers` etc. are arrays of objects — defensive).
    if (Array.isArray(value)) {
      out[key] = value.map((item) =>
        item !== null && typeof item === "object"
          ? sanitizeCtmPii(item as Record<string, unknown>, depth + 1)
          : item,
      );
      continue;
    }

    out[key] = value;
  }
  return out;
}

/**
 * Specialized redactor for the nested `webvisit` object. The `webvisit`
 * shape carries `location_host` + `location_path` fields that often
 * encode caller-PII as URL query params (e.g. `?email=...` or
 * `?phone=...`). The standard `sanitizeCtmPii` recursion would not
 * catch these because they're not in the top-level PII allowlist.
 */
function sanitizeWebvisitNested(
  record: Record<string, unknown>,
  depth: number,
): Record<string, unknown> {
  if (depth > 4) return record;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (FORBIDDEN_KEYS.has(key)) continue;
    if (CTM_WEBVISIT_PII_FIELDS.has(key)) {
      out[key] = "[redacted]";
      continue;
    }
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      out[key] = sanitizeWebvisitNested(
        value as Record<string, unknown>,
        depth + 1,
      );
      continue;
    }
    out[key] = value;
  }
  return out;
}

/**
 * Strip CSV-injection prefix characters from a string value. Round 3
 * Red Team Attack #4 — defensive against pathological customer-supplied
 * data (e.g. `name = "=cmd|/c calc!A1"`) that would execute as a
 * formula in Excel/Google Sheets when the customer exports the report.
 *
 * Mirrors CallRail 22.1's `stripCsvInjectionPrefix` shape — adapter-
 * internal utilities are kept self-contained to avoid cross-adapter
 * coupling per the OAuth-Integration directive's anti-pattern §1. A
 * future shared `packages/integrations/src/safe-export.ts` could lift
 * this once a third consumer emerges.
 *
 * LOOP-strips to handle nested triggers like `=+@cmd` → `cmd` (Round 5
 * Edge Case Hunter finding from CallRail 22.1 carry-forward). Includes
 * `\n` in the trigger set (multi-line formula injection defense in some
 * BI tools).
 */
export function stripCsvInjectionPrefix(value: string): string {
  if (typeof value !== "string" || value.length === 0) return value;
  let out = value;
  while (out.length > 0 && /^[=+@\-\t\r\n]/.test(out)) {
    out = out.slice(1);
  }
  return out;
}
